import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

type DynamicsType = 'os' | 'labs' | 'applicants' | 'manufacturers';

interface DynamicsDetailPageProps {
    getDynamicsData: () => {
        years: string[];
        names: string[];
        counts: Map<string, Map<string, number>>;
    };
    title: string;
    chartTitle: string;
    maps?: {
        innToName?: Map<string, string>;
        nameToInn?: Map<string, string>;
        innToRegion?: Map<string, string>;
        nameToRegion?: Map<string, string>;
        nameToNumber?: Map<string, string>;
        nameToOsIdFsa?: Map<string, string>;
    };
}

const DynamicsDetailPage: React.FC<DynamicsDetailPageProps> = ({
    getDynamicsData,
    title,
    chartTitle,
    maps,
}) => {
    const navigate = useNavigate();
    const dynamicsData = useMemo(() => getDynamicsData(), [getDynamicsData]);
    const [selectedYear, setSelectedYear] = useState<string | null>(null);

    const seriesColors = [
        'rgba(126, 178, 70, 1)',
        'rgba(237, 93, 98, 1)',
        'rgba(255, 166, 0, 1)',
        '#8884d8',
        '#82ca9d',
        'rgba(255, 166, 0, 1)',
        'rgba(126, 178, 70, 1)',
        'rgba(237, 93, 98, 1)',
        'rgba(255, 166, 0, 1)',
        '#8884d8',
    ];

    const tableData = useMemo(() => {
        const data: Array<{
            name: string;
            inn?: string;
            rossCode?: string;
            osIdFsa?: string;
            year: string;
            count: number;
            percent: number;
        }> = [];

        dynamicsData.years.forEach(year => {
            const yearCounts = dynamicsData.counts.get(year);
            if (!yearCounts) return;

            const yearTotal = Array.from(yearCounts.values()).reduce((sum, count) => sum + count, 0);

            yearCounts.forEach((count, name) => {
                const innNameMatch = name.match(/^(\d{10,})\s*-\s*(.+)$/);
                let displayName = name;
                let inn: string | undefined;
                let rossCode: string | undefined;
                let osIdFsa: string | undefined;

                const numberMatch = name.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
                if (numberMatch) {
                    displayName = numberMatch[1].trim();
                    rossCode = numberMatch[2].trim();
                } else if (innNameMatch) {
                    inn = innNameMatch[1];
                    displayName = innNameMatch[2];
                } else if (maps) {
                    const isInn = /^\d{10,}$/.test(name);
                    if (isInn && maps.innToName) {
                        displayName = maps.innToName.get(name) || name;
                        inn = name;
                    } else if (maps.nameToInn) {
                        inn = maps.nameToInn.get(name);
                    }
                }

                if (maps?.nameToOsIdFsa) {
                    osIdFsa = maps.nameToOsIdFsa.get(name);
                }

                data.push({
                    name: displayName,
                    inn,
                    rossCode,
                    osIdFsa,
                    year,
                    count,
                    percent: yearTotal > 0 ? (count / yearTotal) * 100 : 0,
                });
            });
        });

        return data.sort((a, b) => {
            if (a.year !== b.year) return b.year.localeCompare(a.year);
            return b.count - a.count;
        });
    }, [dynamicsData, maps]);

    // Фильтрация данных для таблицы
    const filteredTableData = useMemo(() => {
        if (selectedYear) {
            // Если выбран конкретный год -  только данные за этот год
            return tableData.filter(item => item.year === selectedYear);
        } else {
            // Если выбраны все года - сумма данных по всем годам каждой организации
            const aggregated = new Map<string, {
                name: string;
                inn?: string;
                rossCode?: string;
                osIdFsa?: string;
                count: number;
                year: string;
            }>();

            tableData.forEach(item => {
                const key = `${item.name}|${item.inn || ''}|${item.rossCode || ''}`;
                if (aggregated.has(key)) {
                    const existing = aggregated.get(key)!;
                    existing.count += item.count;
                } else {
                    aggregated.set(key, {
                        name: item.name,
                        inn: item.inn,
                        rossCode: item.rossCode,
                        osIdFsa: item.osIdFsa,
                        count: item.count,
                        year: 'Все годы'
                    });
                }
            });

            const totalCount = Array.from(aggregated.values()).reduce((sum, item) => sum + item.count, 0);

            return Array.from(aggregated.values()).map(item => ({
                ...item,
                percent: totalCount > 0 ? (item.count / totalCount) * 100 : 0,
            })).sort((a, b) => b.count - a.count);
        }
    }, [tableData, selectedYear]);

    const showInn = maps && !(maps as any).nameToNumber && (maps.nameToInn || maps.innToName);

    // Экспорт в Excel
    const handleExportToExcel = () => {
        const exportData = filteredTableData.map(item => ({
            'Наименование': item.name,
            ...(item.rossCode && { 'Номер': item.rossCode }),
            ...(item.inn && showInn && { 'ИНН': item.inn }),
            'Год': item.year,
            'Количество': item.count,
            'Доля (%)': Number(item.percent.toFixed(1)),
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Данные');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(dataBlob, `${title}_динамика.xlsx`);
    };

    const allNames = useMemo(() => dynamicsData.names
        .sort((a, b) => {
            const sum = (name: string) => dynamicsData.years.reduce((s, y) => 
                s + (dynamicsData.counts.get(y)?.get(name) || 0), 0);
            const diff = sum(b) - sum(a);
            return diff !== 0 ? diff : a.localeCompare(b);
        }), [dynamicsData]);

    const topNames = useMemo(() => allNames.slice(0, 20), [allNames]);

    const chartDataAll = dynamicsData.years.map(year => {
        const row: Record<string, number | string> = { year: String(year) };
        topNames.forEach(name => {
            row[name] = dynamicsData.counts.get(year)?.get(name) || 0;
        });
        return row;
    });

    const hasData = useMemo(() => {
        return dynamicsData.years.some(y => {
            const m = dynamicsData.counts.get(y);
            if (!m) return false;
            return Array.from(m.values()).some(v => v > 0);
        });
    }, [dynamicsData]);

    const chartData = useMemo(() => {
        if (selectedYear) {
            return chartDataAll.filter(d => String(d.year) === String(selectedYear));
        }
        return chartDataAll;
    }, [chartDataAll, selectedYear]);

    const [chartType, setChartType] = useState<'bar' | 'line'>('bar');

    const lineData = useMemo(() => {
        return allNames.map(name => ({
            id: name,
            data: dynamicsData.years.map(y => ({ x: y, y: dynamicsData.counts.get(y)?.get(name) || 0 }))
        }));
    }, [allNames, dynamicsData]);

    const [visibleSeries, setVisibleSeries] = useState<Set<string>>(new Set());
    useEffect(() => {
        if (visibleSeries.size === 0 && allNames.length > 0) {
            setVisibleSeries(new Set(allNames));
        }
    }, [allNames]);

    const colorMap: Record<string, string> = useMemo(() => {
        const map: Record<string, string> = {};
        allNames.forEach((n, i) => { map[n] = seriesColors[i % seriesColors.length]; });
        return map;
    }, [allNames]);

    const handleToggleSerie = (id: string) => {
        setVisibleSeries(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleSelectAll = () => setVisibleSeries(new Set(allNames));
    const handleSelectNone = () => setVisibleSeries(new Set());

    const handleToggleRange = (startIdx: number, endIdx: number, makeVisible: boolean) => {
        setVisibleSeries(prev => {
            const next = new Set(prev);
            const slice = allNames.slice(startIdx, endIdx);
            slice.forEach(name => {
                if (makeVisible) next.add(name); else next.delete(name);
            });
            return next;
        });
    };

    const [orgSearch, setOrgSearch] = useState<string>('');

    const [isMobile, setIsMobile] = useState<boolean>(false);
    const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 740);
            setIsSmallScreen(window.innerWidth < 768);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
            <div style={{ padding: '20px', maxWidth: '1600px', margin: '0 auto' }}>
                {!hasData && (
                    <div style={{
                        marginTop: 12,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: 24,
                        textAlign: 'center',
                        color: '#374151'
                    }}>
                        <h3 style={{ margin: 0, marginBottom: 8 }}>Нет результатов</h3>
                        <div>По выбранным фильтрам не найдено данных. Измените параметры и попробуйте снова.</div>
                    </div>
                )}
                {hasData && (isMobile ? (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <button
                                onClick={() => navigate('/certificates')}
                                style={{
                                    padding: '6px 10px',
                                    backgroundColor: '#f0f0f0',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                }}
                            >
                                ← Назад
                            </button>
                            <button
                                onClick={handleExportToExcel}
                                style={{
                                    padding: '6px 10px',
                                    backgroundColor: '#1a73e8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                }}
                            >
                                Экспорт в Excel
                            </button>
                        </div>
                        <h1 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', textAlign: 'center' }}>{title}</h1>
                    </>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <button
                            onClick={() => navigate('/certificates')}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#f0f0f0',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                            }}
                        >
                            ← Назад
                        </button>
                        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>{title}</h1>
                        <button
                            onClick={handleExportToExcel}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#1a73e8',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                            }}
                        >
                            Экспорт в Excel
                        </button>
                    </div>
                ))}

                {/* График */}
                {hasData && (
                <div style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    marginBottom: '30px',
                    minHeight: '600px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 12, flexWrap: 'wrap', rowGap: 8 }}>
                        <div style={{ display: 'inline-flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, color: '#374151' }}>График:</span>
                            <select value={chartType} onChange={(e) => setChartType(e.target.value as 'bar'|'line')} style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4, background: '#fff' }}>
                                <option value="bar">Столбчатый</option>
                                <option value="line">Линейный</option>
                            </select>
                            {selectedYear && (
                                <>
                                    <span style={{ fontSize: 13, color: '#374151' }}>Год: <strong>{selectedYear}</strong></span>
                                    <button onClick={() => setSelectedYear(null)} style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4, background: '#f8fafc', cursor: 'pointer' }}>Сбросить</button>
                                </>
                            )}
                        </div>
                    </div>
                    {chartType === 'bar' ? (
                        <ResponsiveBar
                            data={chartData}
                            keys={topNames}
                            indexBy="year"
                            margin={{ top: 40, right: isMobile ? 30 : 20, bottom: isMobile ? 60 : 80, left: isMobile ? 60 : 80 }}
                            padding={0.1}
                            groupMode="stacked"
                        colors={(bar) => {
                            const idx = topNames.indexOf(String(bar.id));
                            const base = seriesColors[idx % seriesColors.length];
                            const y = String((bar.data as any).year || '');
                            if (selectedYear && y !== selectedYear) {
                                return '#e5e7eb';
                            }
                            return base;
                        }}
                            borderRadius={4}
                            enableLabel={true}
                            labelSkipWidth={16}
                            labelSkipHeight={16}
                            labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: 'Год',
                                legendPosition: 'middle',
                                legendOffset: 40,
                                truncateTickAt: 0,
                            }}
                            axisLeft={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: 'Количество сертификатов',
                                legendPosition: 'middle',
                                legendOffset: -50,
                            }}
                        onClick={(bar) => {
                            const year = String((bar as any).indexValue ?? (bar.data as any).year ?? '');
                            if (!year) return;
                            setSelectedYear(prev => (prev === year ? null : year));
                        }}
                        borderWidth={selectedYear ? 1 : 0}
                        borderColor="#111827"
                        legends={[]}
                            animate={true}
                            motionConfig="gentle"
                        />
                    ) : (
                        <>
                            <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                                <button onClick={handleSelectAll} style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4, background: '#f8fafc', cursor: 'pointer' }}>Показать все</button>
                                <button onClick={handleSelectNone} style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4, background: '#f8fafc', cursor: 'pointer' }}>Скрыть все</button>
                                <details>
                                    <summary style={{ cursor: 'pointer', userSelect: 'none' }}>Показать с...</summary>
                                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {Array.from({ length: Math.ceil(allNames.length / 10) }).map((_, i) => {
                                            const start = i * 10;
                                            const end = Math.min(start + 10, allNames.length);
                                            const label = `${start + 1}-${end}`;
                                            return (
                                                <button key={`show-${i}`} onClick={() => handleToggleRange(start, end, true)} style={{ padding: '6px 8px', border: '1px solid #93c5fd', borderRadius: 4, background: '#eff6ff', cursor: 'pointer' }}>Показать {label}</button>
                                            );
                                        })}
                                    </div>
                                </details>
                                <details>
                                    <summary style={{ cursor: 'pointer', userSelect: 'none' }}>Скрыть с...</summary>
                                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {Array.from({ length: Math.ceil(allNames.length / 10) }).map((_, i) => {
                                            const start = i * 10;
                                            const end = Math.min(start + 10, allNames.length);
                                            const label = `${start + 1}-${end}`;
                                            return (
                                                <button key={`hide-${i}`} onClick={() => handleToggleRange(start, end, false)} style={{ padding: '6px 8px', border: '1px solid #fdba74', borderRadius: 4, background: '#fff7ed', cursor: 'pointer' }}>Скрыть {label}</button>
                                            );
                                        })}
                                    </div>
                                </details>
                            </div>
                            <div style={{ height: isMobile ? 380 : 640 }}>
                                <ResponsiveLine
                                    data={lineData.filter(s => visibleSeries.has(String(s.id))).reverse()}
                                    margin={{ top: 20, right: isMobile ? 20 : 30, bottom: isMobile ? 40 : 60, left: isMobile ? 40 : 70 }}
                                    xScale={{ type: 'point' }}
                                    yScale={{ type: 'linear', stacked: false, min: 'auto', max: 'auto' }}
                                    curve="monotoneX"
                                    colors={(serie) => colorMap[String(serie.id)]}
                                    lineWidth={3}
                                    pointSize={isMobile ? 4 : 6}
                                    pointColor={{ from: 'serieColor' }}
                                    pointBorderWidth={2}
                                    pointBorderColor="#ffffff"
                                    enableArea={true}
                                    areaOpacity={1}
                                    enableSlices={false}
                                    tooltip={({ point }) => {
                                        const yearTotal = dynamicsData.counts.get(String(point.data.x)) 
                                            ? Array.from(dynamicsData.counts.get(String(point.data.x))!.values()).reduce((sum, count) => sum + count, 0)
                                            : 0;
                                        const count = Number(point.data.y);
                                        const percent = yearTotal > 0 ? (count / yearTotal) * 100 : 0;
                                        return (
                                            <div style={{
                                                background: 'white',
                                                padding: '12px',
                                                border: '1px solid #ccc',
                                                borderRadius: '6px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                maxWidth: '250px',
                                            }}>
                                                <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>{String(point.seriesId)}</div>
                                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>Год: {point.data.x}</div>
                                                <div style={{ fontSize: '13px', color: '#666' }}>
                                                    Кол-во: {count} ({percent.toFixed(1)}%)
                                                </div>
                                            </div>
                                        );
                                    }}
                                    axisBottom={{ legend: 'Год', legendOffset: isMobile ? 28 : 40, legendPosition: 'middle' }}
                                    axisLeft={{ legend: 'Количество', legendOffset: isMobile ? -40 : -50, legendPosition: 'middle' }}
                                    useMesh={true}
                                />
                            </div>
                        </>
                    )}
                </div>
                )}

                {/* Блок организаций */}
                {hasData && chartType === 'line' && (
                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        marginBottom: '20px',
                    }}>
                        <details>
                            <summary style={{ fontSize: '14px', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>
                                Организации ({dynamicsData.names.length})
                            </summary>
                        <div style={{ marginTop: 10, marginBottom: 10 }}>
                            <input
                                value={orgSearch}
                                onChange={(e) => setOrgSearch(e.target.value)}
                                placeholder="Поиск..."
                                aria-label="Поиск организаций"
                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
                            />
                        </div>
                        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, maxHeight: isMobile ? 300 : 500, overflowY: 'auto' }}>
                            {allNames.filter(n => n.toLowerCase().includes(orgSearch.toLowerCase()))
                                .map((name, idx) => (
                                <label key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={chartType === 'line' ? visibleSeries.has(name) : true}
                                        onChange={() => chartType === 'line' ? handleToggleSerie(name) : undefined}
                                        disabled={chartType !== 'line'}
                                    />
                                    <span style={{ display: 'inline-block', width: 10, height: 10, background: seriesColors[idx % seriesColors.length], borderRadius: 2 }} />
                                    <span style={{ fontSize: 13 }}>{name}</span>
                                </label>
                            ))}
                        </div>
                        </details>
                    </div>
                )}

                {/* Таблица */}
                {hasData && (
                <div style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Таблица данных</h2>
                        <select
                            value={selectedYear || ''}
                            onChange={(e) => setSelectedYear(e.target.value || null)}
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer',
                            }}
                        >
                            <option value="">Все годы</option>
                            {dynamicsData.years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '14px',
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Наименование</th>
                                    {filteredTableData.some(item => item.rossCode) && (
                                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Номер</th>
                                    )}
                                    {showInn && (
                                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>ИНН</th>
                                    )}
                                    {maps && (maps as any).nameToRegion && (
                                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Регион</th>
                                    )}
                                  
                                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Количество</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Доля (%)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTableData.map((item, idx) => (
                                    <tr
                                        key={`${item.name}-${item.year}-${idx}`}
                                        style={{
                                            borderBottom: '1px solid #eee',
                                            backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa',
                                        }}
                                    >
                                        <td style={{ padding: '12px' }}>{item.name}</td>
                                        {showInn && (
                                            <td style={{ padding: '12px' }}>{item.inn || '-'}</td>
                                        )}
                                        {maps && (maps as any).nameToRegion && (
                                            <td style={{ padding: '12px' }}>{
                                                (item.inn && (maps as any).innToRegion && (maps as any).innToRegion.get(item.inn))
                                                || ((maps as any).nameToRegion && (maps as any).nameToRegion.get(item.name))
                                                || '-'
                                            }</td>
                                        )}
                                        {(() => {
                                            if (maps && (maps as any).nameToNumber) {
                                                const number = ((maps as any).nameToNumber.get(item.name));
                                                return (
                                                    <td style={{ padding: '12px' }}>
                                                        {item.osIdFsa && number ? (
                                                            <a
                                                                href={`https://pub.fsa.gov.ru/ral/view/${item.osIdFsa}/applicant`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: '#1a73e8', textDecoration: 'underline' }}
                                                            >
                                                                {number}
                                                            </a>
                                                        ) : (number || '-')}
                                                    </td>
                                                );
                                            }
                                            if (item.osIdFsa && item.rossCode) {
                                                return (
                                                    <td style={{ padding: '12px' }}>
                                                        <a
                                                            href={`https://pub.fsa.gov.ru/ral/view/${item.osIdFsa}/applicant`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: '#1a73e8', textDecoration: 'underline' }}
                                                        >
                                                            {item.rossCode}
                                                        </a>
                                                    </td>
                                                );
                                            }
                                            return null;
                                        })()}
                                        <td style={{ padding: '12px', textAlign: 'right' }}>{item.count}</td>
                                        <td style={{ padding: '12px', textAlign: 'right' }}>{item.percent.toFixed(1)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {filteredTableData.length === 0 && (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                            Нет данных для отображения
                        </div>
                    )}
                </div>
                )}
            </div>
    );
};

export default DynamicsDetailPage;

